import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { createRequestStatusObject, buildReducers } from '../utils';
import type {
	EmailBrandingValue,
	EmailPlaceholder,
	EmailTemplateKey,
	EmailTemplateValue
} from '../../constants/emailTemplates';

/**
 * Customisable emails (PTEC-895): platform defaults (App Admin) and organisation
 * overrides (Org Admin), served by the organisations Lambda.
 *
 * Every call to that API is made here. Preview and test send are thunks too, but
 * keep nothing in the store: their results are transient and belong to the page.
 */

export interface EmailDefaultTemplate {
	label: string;
	orgOverridable: boolean;
	hasButton: boolean;
	placeholders: EmailPlaceholder[];
	/** Stored fields only; absent fields inherit `builtIn`. */
	value: EmailTemplateValue;
	/** 0 when nothing is stored. */
	version: number;
	/** The Pearsana built-in copy. */
	builtIn: EmailTemplateValue;
}

export interface EmailDefaults {
	templates: Partial<Record<EmailTemplateKey, EmailDefaultTemplate>>;
	branding: { value: EmailBrandingValue; version: number; builtIn: EmailBrandingValue };
}

export interface OrgEmailTemplate {
	label: string;
	hasButton: boolean;
	placeholders: EmailPlaceholder[];
	override: EmailTemplateValue;
	inherited: EmailTemplateValue;
	effective: EmailTemplateValue;
	version: number;
}

export interface OrgEmailSettings {
	entitled: boolean;
	orgName: string;
	logo: { logoUrl: string; sourceKind?: 'orgBranding' | 'profile' | null } | null;
	templates: Partial<Record<EmailTemplateKey, OrgEmailTemplate>>;
	branding: {
		override: EmailBrandingValue;
		inherited: EmailBrandingValue;
		effective: EmailBrandingValue;
		version: number;
	};
}

/**
 * What a rejected write carries. A thrown error is serialised down to
 * name/message/stack, which loses the response body — so the status, code,
 * field errors and a 409's current version are carried out deliberately.
 */
export interface EmailApiError {
	status?: number;
	code?: string;
	message?: string;
	errors?: { field: string; code: string; message?: string }[];
	current?: { value: EmailTemplateValue | EmailBrandingValue; version: number };
}

type ErrorBody = {
	message?: string;
	code?: string;
	errors?: EmailApiError['errors'];
	current?: EmailApiError['current'];
	value?: EmailTemplateValue | EmailBrandingValue;
	version?: number;
};

export const toEmailApiError = (error: unknown): EmailApiError => {
	const response = (error as { response?: { status?: number; data?: ErrorBody } })?.response;
	const data = response?.data ?? {};
	// The 409 body spreads the current record's { value, version } alongside the code.
	const current =
		data.current ?? (data.value !== undefined ? { value: data.value, version: data.version ?? 0 } : undefined);
	return {
		status: response?.status,
		code: data.code,
		message: data.message ?? (error as { message?: string })?.message,
		errors: data.errors,
		current
	};
};

// No typed `rejectValue`: buildReducers only accepts thunks whose rejection payload
// is untyped. Every thunk below rejects with an EmailApiError (see toEmailApiError),
// which is what `unwrap()` throws to the caller.
type ThunkConfig = { dispatch: AppDispatch; state: RootState };

interface EmailTemplatesState {
	defaults: EmailDefaults | null;
	/** Organisation settings keyed by organisation id. */
	orgSettings: Record<string, OrgEmailSettings>;
	defaultsRequest: ReturnType<typeof createRequestStatusObject>;
	orgSettingsRequest: ReturnType<typeof createRequestStatusObject>;
	saveRequest: ReturnType<typeof createRequestStatusObject>;
	resetRequest: ReturnType<typeof createRequestStatusObject>;
}

const initialState: EmailTemplatesState = {
	defaults: null,
	orgSettings: {},
	defaultsRequest: createRequestStatusObject(),
	orgSettingsRequest: createRequestStatusObject(),
	saveRequest: createRequestStatusObject(),
	resetRequest: createRequestStatusObject()
};

const DEFAULTS_PATH = '/organisations/email-defaults';
const orgPath = (organisationId: string) => `/organisations/${organisationId}/email-settings`;

// ------------------------------------------------------------ platform defaults

export const fetchEmailDefaults = createAsyncThunk<EmailDefaults, void, ThunkConfig>(
	'emailTemplates/fetchDefaults',
	async (_arg, { rejectWithValue }) => {
		try {
			return (await API.get('inspirusapi', DEFAULTS_PATH, {})) as EmailDefaults;
		} catch (error) {
			return rejectWithValue(toEmailApiError(error));
		}
	}
);

export const saveEmailDefaultTemplate = createAsyncThunk<
	{ templateKey: EmailTemplateKey; value: EmailTemplateValue; version: number },
	{ templateKey: EmailTemplateKey; value: EmailTemplateValue; version: number },
	ThunkConfig
>('emailTemplates/saveDefaultTemplate', async ({ templateKey, value, version }, { rejectWithValue }) => {
	try {
		const saved = await API.put('inspirusapi', `${DEFAULTS_PATH}/templates/${templateKey}`, {
			headers: {},
			body: { value, version }
		});
		return { templateKey, value: saved.value, version: saved.version };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

export const resetEmailDefaultTemplate = createAsyncThunk<
	{ templateKey: EmailTemplateKey },
	{ templateKey: EmailTemplateKey },
	ThunkConfig
>('emailTemplates/resetDefaultTemplate', async ({ templateKey }, { rejectWithValue }) => {
	try {
		await API.del('inspirusapi', `${DEFAULTS_PATH}/templates/${templateKey}`, {});
		return { templateKey };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

export const saveEmailDefaultBranding = createAsyncThunk<
	{ value: EmailBrandingValue; version: number },
	{ value: EmailBrandingValue; version: number },
	ThunkConfig
>('emailTemplates/saveDefaultBranding', async ({ value, version }, { rejectWithValue }) => {
	try {
		const saved = await API.put('inspirusapi', `${DEFAULTS_PATH}/branding`, {
			headers: {},
			body: { value, version }
		});
		return { value: saved.value, version: saved.version };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

export const resetEmailDefaultBranding = createAsyncThunk<void, void, ThunkConfig>(
	'emailTemplates/resetDefaultBranding',
	async (_arg, { rejectWithValue }) => {
		try {
			await API.del('inspirusapi', `${DEFAULTS_PATH}/branding`, {});
		} catch (error) {
			return rejectWithValue(toEmailApiError(error));
		}
	}
);

// ------------------------------------------------------------ organisation overrides

export const fetchOrgEmailSettings = createAsyncThunk<
	{ organisationId: string; settings: OrgEmailSettings },
	{ organisationId: string },
	ThunkConfig
>('emailTemplates/fetchOrgSettings', async ({ organisationId }, { rejectWithValue }) => {
	try {
		const settings = (await API.get('inspirusapi', orgPath(organisationId), {})) as OrgEmailSettings;
		return { organisationId, settings };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

export const saveOrgEmailTemplate = createAsyncThunk<
	{ organisationId: string; templateKey: EmailTemplateKey; value: EmailTemplateValue; version: number },
	{ organisationId: string; templateKey: EmailTemplateKey; value: EmailTemplateValue; version: number },
	ThunkConfig
>('emailTemplates/saveOrgTemplate', async ({ organisationId, templateKey, value, version }, { rejectWithValue }) => {
	try {
		const saved = await API.put('inspirusapi', `${orgPath(organisationId)}/templates/${templateKey}`, {
			headers: {},
			body: { value, version }
		});
		return { organisationId, templateKey, value: saved.value, version: saved.version };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

export const resetOrgEmailTemplate = createAsyncThunk<
	{ organisationId: string; templateKey: EmailTemplateKey },
	{ organisationId: string; templateKey: EmailTemplateKey },
	ThunkConfig
>('emailTemplates/resetOrgTemplate', async ({ organisationId, templateKey }, { rejectWithValue }) => {
	try {
		await API.del('inspirusapi', `${orgPath(organisationId)}/templates/${templateKey}`, {});
		return { organisationId, templateKey };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

export const saveOrgEmailBranding = createAsyncThunk<
	{ organisationId: string; value: EmailBrandingValue; version: number },
	{ organisationId: string; value: EmailBrandingValue; version: number },
	ThunkConfig
>('emailTemplates/saveOrgBranding', async ({ organisationId, value, version }, { rejectWithValue }) => {
	try {
		const saved = await API.put('inspirusapi', `${orgPath(organisationId)}/branding`, {
			headers: {},
			body: { value, version }
		});
		return { organisationId, value: saved.value, version: saved.version };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

export const resetOrgEmailBranding = createAsyncThunk<
	{ organisationId: string },
	{ organisationId: string },
	ThunkConfig
>('emailTemplates/resetOrgBranding', async ({ organisationId }, { rejectWithValue }) => {
	try {
		await API.del('inspirusapi', `${orgPath(organisationId)}/branding`, {});
		return { organisationId };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

// ---------------------------------------------------------- preview and test

/** A rendered email, exactly as the senders would produce it. */
export interface EmailPreviewResult {
	subject: string;
	html: string;
	text: string;
	length: number;
}

/**
 * Which level a preview or test send is for: a platform default (optionally
 * shown as a given organisation would receive it) or an organisation's override.
 */
export type EmailDraftScope =
	| { kind: 'platform'; sampleOrganisationId?: string }
	| { kind: 'organisation'; organisationId: string };

export interface EmailDraftRequest {
	scope: EmailDraftScope;
	templateKey: EmailTemplateKey;
	template?: EmailTemplateValue;
	branding?: EmailBrandingValue;
}

const draftPath = (scope: EmailDraftScope, templateKey: EmailTemplateKey) =>
	`${scope.kind === 'platform' ? DEFAULTS_PATH : orgPath(scope.organisationId)}/templates/${templateKey}`;

const draftBody = ({ scope, template, branding }: EmailDraftRequest) => ({
	...(template ? { template } : {}),
	...(branding ? { branding } : {}),
	...(scope.kind === 'platform' && scope.sampleOrganisationId
		? { sampleOrganisationId: scope.sampleOrganisationId }
		: {})
});

export const previewEmailTemplate = createAsyncThunk<EmailPreviewResult, EmailDraftRequest, ThunkConfig>(
	'emailTemplates/preview',
	async (request, { rejectWithValue }) => {
		try {
			return (await API.post('inspirusapi', `${draftPath(request.scope, request.templateKey)}/preview`, {
				body: draftBody(request)
			})) as EmailPreviewResult;
		} catch (error) {
			return rejectWithValue(toEmailApiError(error));
		}
	}
);

export const sendTestEmail = createAsyncThunk<
	{ sentTo: string[] },
	EmailDraftRequest & { recipients: string[] },
	ThunkConfig
>('emailTemplates/sendTest', async ({ recipients, ...request }, { rejectWithValue }) => {
	try {
		return (await API.post('inspirusapi', `${draftPath(request.scope, request.templateKey)}/test`, {
			body: { ...draftBody(request), recipients }
		})) as { sentTo: string[] };
	} catch (error) {
		return rejectWithValue(toEmailApiError(error));
	}
});

// Mirrors the backend's field merge closely enough for display until the next
// read: a stored field wins, an absent one inherits. The next GET is authoritative.
const merge = <T extends object>(override: T, inherited: T): T => {
	const result = { ...inherited } as Record<string, unknown>;
	Object.entries(override).forEach(([field, value]) => {
		if (value !== undefined) {
			result[field] = value;
		}
	});
	return result as T;
};

const emailTemplatesSlice = createSlice({
	name: 'emailTemplates',
	initialState,
	reducers: {
		resetEmailTemplates: () => initialState
	},
	extraReducers: (builder) => {
		buildReducers(builder, fetchEmailDefaults, 'defaultsRequest', {
			onFulfilled: (state: EmailTemplatesState, payload) => {
				state.defaults = payload;
			}
		});
		buildReducers(builder, saveEmailDefaultTemplate, 'saveRequest', {
			onFulfilled: (state: EmailTemplatesState, { templateKey, value, version }) => {
				const template = state.defaults?.templates[templateKey];
				if (template) {
					template.value = value;
					template.version = version;
				}
			}
		});
		buildReducers(builder, resetEmailDefaultTemplate, 'resetRequest', {
			onFulfilled: (state: EmailTemplatesState, { templateKey }) => {
				const template = state.defaults?.templates[templateKey];
				if (template) {
					template.value = {};
					template.version = 0;
				}
			}
		});
		buildReducers(builder, saveEmailDefaultBranding, 'saveRequest', {
			onFulfilled: (state: EmailTemplatesState, { value, version }) => {
				if (state.defaults) {
					state.defaults.branding.value = value;
					state.defaults.branding.version = version;
				}
			}
		});
		buildReducers(builder, resetEmailDefaultBranding, 'resetRequest', {
			onFulfilled: (state: EmailTemplatesState) => {
				if (state.defaults) {
					state.defaults.branding.value = {};
					state.defaults.branding.version = 0;
				}
			}
		});

		buildReducers(builder, fetchOrgEmailSettings, 'orgSettingsRequest', {
			onFulfilled: (state: EmailTemplatesState, { organisationId, settings }) => {
				state.orgSettings[organisationId] = settings;
			}
		});
		buildReducers(builder, saveOrgEmailTemplate, 'saveRequest', {
			onFulfilled: (state: EmailTemplatesState, { organisationId, templateKey, value, version }) => {
				const template = state.orgSettings[organisationId]?.templates[templateKey];
				if (template) {
					template.override = value;
					template.version = version;
					template.effective = merge(value, template.inherited);
				}
			}
		});
		buildReducers(builder, resetOrgEmailTemplate, 'resetRequest', {
			onFulfilled: (state: EmailTemplatesState, { organisationId, templateKey }) => {
				const template = state.orgSettings[organisationId]?.templates[templateKey];
				if (template) {
					template.override = {};
					template.version = 0;
					template.effective = template.inherited;
				}
			}
		});
		buildReducers(builder, saveOrgEmailBranding, 'saveRequest', {
			onFulfilled: (state: EmailTemplatesState, { organisationId, value, version }) => {
				const branding = state.orgSettings[organisationId]?.branding;
				if (branding) {
					branding.override = value;
					branding.version = version;
					branding.effective = merge(value, branding.inherited);
				}
			}
		});
		buildReducers(builder, resetOrgEmailBranding, 'resetRequest', {
			onFulfilled: (state: EmailTemplatesState, { organisationId }) => {
				const branding = state.orgSettings[organisationId]?.branding;
				if (branding) {
					branding.override = {};
					branding.version = 0;
					branding.effective = branding.inherited;
				}
			}
		});
	}
});

export const { resetEmailTemplates } = emailTemplatesSlice.actions;
export default emailTemplatesSlice.reducer;
